import React, { useState, useEffect } from 'react';
import { BarChart3, Users, TrendingUp, Download, Calendar, CheckCircle, Clock } from 'lucide-react';
import { supabase } from '../supabase';
import { formatCurrency, getMonthLabel, getLastNMonths, getCurrentPeriodMonth } from '../utils/commissionCalc';

export default function Reports() {
  const [loading, setLoading] = useState(true);
  const [affiliateStats, setAffiliateStats] = useState([]);
  const [monthlyTotals, setMonthlyTotals] = useState([]);
  const [monthlyByAffiliate, setMonthlyByAffiliate] = useState([]);
  const [selectedView, setSelectedView] = useState('reconciliation');
  const [selectedMonth, setSelectedMonth] = useState(getCurrentPeriodMonth());
  const [allCommissions, setAllCommissions] = useState([]);
  const [affiliates, setAffiliates] = useState([]);

  const months = getLastNMonths(12);

  useEffect(() => {
    loadReportData();
  }, []);

  useEffect(() => {
    if (allCommissions.length > 0 && affiliates.length > 0) {
      calculateMonthlyByAffiliate();
    }
  }, [selectedMonth, allCommissions, affiliates]);

  const loadReportData = async () => {
    try {
      // Load all commissions
      const { data: commissions } = await supabase
        .from('affiliate_commissions')
        .select(`
          *,
          affiliates (id, name, code)
        `)
        .order('period_month', { ascending: false });

      // Load affiliates
      const { data: affiliatesData } = await supabase
        .from('affiliates')
        .select('*')
        .order('name');

      // Load companies for account counts
      const { data: companies } = await supabase
        .from('companies')
        .select('id, referred_by_affiliate_id, subscription_status');

      setAllCommissions(commissions || []);
      setAffiliates(affiliatesData || []);

      // Calculate affiliate stats
      const affiliateStatsMap = {};
      (affiliatesData || []).forEach(affiliate => {
        affiliateStatsMap[affiliate.id] = {
          ...affiliate,
          totalEarned: 0,
          totalPaid: 0,
          totalOwed: 0,
          accountCount: 0,
          activeAccountCount: 0
        };
      });

      // Add commission data
      (commissions || []).forEach(comm => {
        const stats = affiliateStatsMap[comm.affiliate_id];
        if (stats) {
          const amount = parseFloat(comm.commission_amount) || 0;
          stats.totalEarned += amount;
          if (comm.status === 'paid') {
            stats.totalPaid += amount;
          } else {
            stats.totalOwed += amount;
          }
        }
      });

      // Add account counts
      (companies || []).forEach(company => {
        if (company.referred_by_affiliate_id && affiliateStatsMap[company.referred_by_affiliate_id]) {
          affiliateStatsMap[company.referred_by_affiliate_id].accountCount++;
          if (company.subscription_status === 'active') {
            affiliateStatsMap[company.referred_by_affiliate_id].activeAccountCount++;
          }
        }
      });

      // Calculate monthly totals
      const monthlyMap = {};
      months.forEach(month => {
        monthlyMap[month] = { month, owed: 0, paid: 0, total: 0 };
      });

      (commissions || []).forEach(comm => {
        if (monthlyMap[comm.period_month]) {
          const amount = parseFloat(comm.commission_amount) || 0;
          monthlyMap[comm.period_month].total += amount;
          if (comm.status === 'paid') {
            monthlyMap[comm.period_month].paid += amount;
          } else {
            monthlyMap[comm.period_month].owed += amount;
          }
        }
      });

      setAffiliateStats(Object.values(affiliateStatsMap));
      setMonthlyTotals(Object.values(monthlyMap));
    } catch (error) {
      console.error('Error loading report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateMonthlyByAffiliate = () => {
    // Build a map of affiliate stats for the selected month
    const affiliateMonthMap = {};

    affiliates.forEach(affiliate => {
      affiliateMonthMap[affiliate.id] = {
        affiliate,
        owed: 0,
        paid: 0,
        total: 0,
        commissionCount: 0
      };
    });

    // Sum up commissions for selected month
    allCommissions.forEach(comm => {
      if (comm.period_month === selectedMonth && affiliateMonthMap[comm.affiliate_id]) {
        const amount = parseFloat(comm.commission_amount) || 0;
        affiliateMonthMap[comm.affiliate_id].total += amount;
        affiliateMonthMap[comm.affiliate_id].commissionCount++;
        if (comm.status === 'paid') {
          affiliateMonthMap[comm.affiliate_id].paid += amount;
        } else {
          affiliateMonthMap[comm.affiliate_id].owed += amount;
        }
      }
    });

    // Convert to array and filter out affiliates with no activity
    const result = Object.values(affiliateMonthMap)
      .filter(stat => stat.total > 0 || stat.commissionCount > 0)
      .sort((a, b) => b.total - a.total);

    setMonthlyByAffiliate(result);
  };

  const exportToCSV = () => {
    let csv = '';

    if (selectedView === 'affiliates') {
      csv = 'Affiliate,Code,Accounts,Active Accounts,Total Earned,Total Paid,Total Owed\n';
      affiliateStats.forEach(stat => {
        csv += `"${stat.name}","${stat.code}",${stat.accountCount},${stat.activeAccountCount},${stat.totalEarned.toFixed(2)},${stat.totalPaid.toFixed(2)},${stat.totalOwed.toFixed(2)}\n`;
      });
    } else if (selectedView === 'monthly') {
      csv = 'Month,Owed,Paid,Total\n';
      monthlyTotals.forEach(month => {
        csv += `"${getMonthLabel(month.month)}",${month.owed.toFixed(2)},${month.paid.toFixed(2)},${month.total.toFixed(2)}\n`;
      });
    } else if (selectedView === 'reconciliation') {
      csv = `Monthly Reconciliation - ${getMonthLabel(selectedMonth)}\n`;
      csv += 'Affiliate,Owed,Paid,Outstanding,Status\n';
      monthlyByAffiliate.forEach(stat => {
        const outstanding = stat.owed;
        const status = outstanding === 0 ? 'Paid Up' : 'Outstanding';
        csv += `"${stat.affiliate.name}",${stat.owed.toFixed(2)},${stat.paid.toFixed(2)},${outstanding.toFixed(2)},${status}\n`;
      });
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workvan-${selectedView}-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Calculate grand totals
  const grandTotals = affiliateStats.reduce(
    (acc, stat) => ({
      earned: acc.earned + stat.totalEarned,
      paid: acc.paid + stat.totalPaid,
      owed: acc.owed + stat.totalOwed,
      accounts: acc.accounts + stat.accountCount
    }),
    { earned: 0, paid: 0, owed: 0, accounts: 0 }
  );

  // Calculate selected month totals
  const monthTotals = monthlyByAffiliate.reduce(
    (acc, stat) => ({
      owed: acc.owed + stat.owed,
      paid: acc.paid + stat.paid,
      total: acc.total + stat.total
    }),
    { owed: 0, paid: 0, total: 0 }
  );

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
        Loading reports...
      </div>
    );
  }

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem 3rem' }}>
      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .reports-table { display: block; overflow-x: auto; }
          .reports-table table { min-width: 500px; }
          .reports-view-toggle { flex-direction: column; }
          .reports-view-toggle button { width: 100%; justify-content: center; }
          .reports-month-selector { flex-direction: column; align-items: stretch !important; }
          .reports-month-selector select { min-width: 100% !important; }
        }
      `}</style>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#e0e0e0',
            marginBottom: '0.5rem'
          }}>
            Reports
          </h1>
          <p style={{ color: '#888' }}>
            Affiliate performance and commission summaries
          </p>
        </div>

        <button
          onClick={exportToCSV}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: '#2a2a2a',
            border: '1px solid #3a3a3a',
            borderRadius: '8px',
            color: '#e0e0e0',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          <Download size={18} />
          Export CSV
        </button>
      </div>

      {/* View Toggle */}
      <div className="reports-view-toggle" style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => setSelectedView('reconciliation')}
          style={{
            padding: '0.75rem 1.5rem',
            background: selectedView === 'reconciliation' ? '#ff6b35' : '#2a2a2a',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <CheckCircle size={18} />
          Monthly Reconciliation
        </button>
        <button
          onClick={() => setSelectedView('affiliates')}
          style={{
            padding: '0.75rem 1.5rem',
            background: selectedView === 'affiliates' ? '#ff6b35' : '#2a2a2a',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <Users size={18} />
          By Affiliate (All Time)
        </button>
        <button
          onClick={() => setSelectedView('monthly')}
          style={{
            padding: '0.75rem 1.5rem',
            background: selectedView === 'monthly' ? '#ff6b35' : '#2a2a2a',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <Calendar size={18} />
          Monthly Totals
        </button>
      </div>

      {/* Monthly Reconciliation View */}
      {selectedView === 'reconciliation' && (
        <>
          {/* Month Selector */}
          <div className="reports-month-selector" style={{
            display: 'flex',
            gap: '1rem',
            marginBottom: '1.5rem',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              style={{
                padding: '0.75rem 1rem',
                background: '#1a1a1a',
                border: '1px solid #2a2a2a',
                borderRadius: '8px',
                color: '#e0e0e0',
                fontWeight: '600',
                fontSize: '1rem',
                minWidth: '200px'
              }}
            >
              {months.map(month => (
                <option key={month} value={month}>{getMonthLabel(month)}</option>
              ))}
            </select>

            {monthTotals.owed === 0 && monthTotals.total > 0 && (
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: '#4ecca320',
                border: '1px solid #4ecca340',
                borderRadius: '8px',
                color: '#4ecca3',
                fontWeight: '600',
                fontSize: '0.9rem'
              }}>
                <CheckCircle size={16} />
                All Paid Up
              </span>
            )}

            {monthTotals.owed > 0 && (
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: '#f39c1220',
                border: '1px solid #f39c1240',
                borderRadius: '8px',
                color: '#f39c12',
                fontWeight: '600',
                fontSize: '0.9rem'
              }}>
                <Clock size={16} />
                {formatCurrency(monthTotals.owed)} Outstanding
              </span>
            )}
          </div>

          {/* Month Summary Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            <div style={{
              background: '#1a1a1a',
              borderRadius: '10px',
              padding: '1.25rem',
              border: '1px solid #2a2a2a',
              borderLeft: '4px solid #f39c12'
            }}>
              <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Owed
              </div>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#f39c12',
                fontFamily: '"JetBrains Mono", monospace'
              }}>
                {formatCurrency(monthTotals.owed)}
              </div>
            </div>

            <div style={{
              background: '#1a1a1a',
              borderRadius: '10px',
              padding: '1.25rem',
              border: '1px solid #2a2a2a',
              borderLeft: '4px solid #4ecca3'
            }}>
              <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Paid
              </div>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#4ecca3',
                fontFamily: '"JetBrains Mono", monospace'
              }}>
                {formatCurrency(monthTotals.paid)}
              </div>
            </div>

            <div style={{
              background: '#1a1a1a',
              borderRadius: '10px',
              padding: '1.25rem',
              border: '1px solid #2a2a2a',
              borderLeft: '4px solid #3498db'
            }}>
              <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Total for Month
              </div>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#3498db',
                fontFamily: '"JetBrains Mono", monospace'
              }}>
                {formatCurrency(monthTotals.total)}
              </div>
            </div>
          </div>

          {/* Affiliate Breakdown Table */}
          <div className="reports-table" style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid #2a2a2a',
            overflow: 'hidden'
          }}>
            {monthlyByAffiliate.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
                No commission records for {getMonthLabel(selectedMonth)}.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                    <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>
                      Affiliate
                    </th>
                    <th style={{ textAlign: 'right', padding: '1rem', color: '#888', fontWeight: '500' }}>
                      Owed
                    </th>
                    <th style={{ textAlign: 'right', padding: '1rem', color: '#888', fontWeight: '500' }}>
                      Paid
                    </th>
                    <th style={{ textAlign: 'right', padding: '1rem', color: '#888', fontWeight: '500' }}>
                      Outstanding
                    </th>
                    <th style={{ textAlign: 'center', padding: '1rem', color: '#888', fontWeight: '500' }}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyByAffiliate.map(stat => {
                    const outstanding = stat.owed;
                    const isPaidUp = outstanding === 0;

                    return (
                      <tr
                        key={stat.affiliate.id}
                        style={{ borderBottom: '1px solid #2a2a2a' }}
                      >
                        <td style={{ padding: '1rem' }}>
                          <div style={{ fontWeight: '600', color: '#e0e0e0' }}>
                            {stat.affiliate.name}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>
                            {stat.affiliate.code}
                          </div>
                        </td>
                        <td style={{
                          padding: '1rem',
                          textAlign: 'right',
                          color: '#f39c12',
                          fontFamily: '"JetBrains Mono", monospace'
                        }}>
                          {formatCurrency(stat.owed)}
                        </td>
                        <td style={{
                          padding: '1rem',
                          textAlign: 'right',
                          color: '#4ecca3',
                          fontFamily: '"JetBrains Mono", monospace'
                        }}>
                          {formatCurrency(stat.paid)}
                        </td>
                        <td style={{
                          padding: '1rem',
                          textAlign: 'right',
                          fontWeight: '700',
                          color: isPaidUp ? '#4ecca3' : '#f39c12',
                          fontFamily: '"JetBrains Mono", monospace'
                        }}>
                          {formatCurrency(outstanding)}
                        </td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.375rem',
                            padding: '0.375rem 0.75rem',
                            background: isPaidUp ? '#4ecca320' : '#f39c1220',
                            color: isPaidUp ? '#4ecca3' : '#f39c12',
                            borderRadius: '6px',
                            fontSize: '0.8rem',
                            fontWeight: '600'
                          }}>
                            {isPaidUp ? <CheckCircle size={14} /> : <Clock size={14} />}
                            {isPaidUp ? 'Paid Up' : 'Outstanding'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* All Time by Affiliate View */}
      {selectedView === 'affiliates' && (
        <>
          {/* Summary Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            <div style={{
              background: '#1a1a1a',
              borderRadius: '10px',
              padding: '1.25rem',
              border: '1px solid #2a2a2a',
              borderLeft: '4px solid #3498db'
            }}>
              <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Total Earned (All Time)
              </div>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#3498db',
                fontFamily: '"JetBrains Mono", monospace'
              }}>
                {formatCurrency(grandTotals.earned)}
              </div>
            </div>

            <div style={{
              background: '#1a1a1a',
              borderRadius: '10px',
              padding: '1.25rem',
              border: '1px solid #2a2a2a',
              borderLeft: '4px solid #4ecca3'
            }}>
              <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Total Paid
              </div>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#4ecca3',
                fontFamily: '"JetBrains Mono", monospace'
              }}>
                {formatCurrency(grandTotals.paid)}
              </div>
            </div>

            <div style={{
              background: '#1a1a1a',
              borderRadius: '10px',
              padding: '1.25rem',
              border: '1px solid #2a2a2a',
              borderLeft: '4px solid #f39c12'
            }}>
              <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Total Owed
              </div>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#f39c12',
                fontFamily: '"JetBrains Mono", monospace'
              }}>
                {formatCurrency(grandTotals.owed)}
              </div>
            </div>

            <div style={{
              background: '#1a1a1a',
              borderRadius: '10px',
              padding: '1.25rem',
              border: '1px solid #2a2a2a',
              borderLeft: '4px solid #9b59b6'
            }}>
              <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Attributed Accounts
              </div>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#9b59b6',
                fontFamily: '"JetBrains Mono", monospace'
              }}>
                {grandTotals.accounts}
              </div>
            </div>
          </div>

          {/* Affiliate Table */}
          <div className="reports-table" style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid #2a2a2a',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                  <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>
                    Affiliate
                  </th>
                  <th style={{ textAlign: 'center', padding: '1rem', color: '#888', fontWeight: '500' }}>
                    Accounts
                  </th>
                  <th style={{ textAlign: 'center', padding: '1rem', color: '#888', fontWeight: '500' }}>
                    Active
                  </th>
                  <th style={{ textAlign: 'right', padding: '1rem', color: '#888', fontWeight: '500' }}>
                    Total Earned
                  </th>
                  <th style={{ textAlign: 'right', padding: '1rem', color: '#888', fontWeight: '500' }}>
                    Paid
                  </th>
                  <th style={{ textAlign: 'right', padding: '1rem', color: '#888', fontWeight: '500' }}>
                    Owed
                  </th>
                </tr>
              </thead>
              <tbody>
                {affiliateStats.map(stat => (
                  <tr
                    key={stat.id}
                    style={{ borderBottom: '1px solid #2a2a2a' }}
                  >
                    <td style={{ padding: '1rem' }}>
                      <div style={{ fontWeight: '600', color: '#e0e0e0' }}>
                        {stat.name}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        {stat.code} - {stat.commission_model === 'fixed'
                          ? formatCurrency(stat.commission_rate) + '/mo'
                          : (stat.commission_rate * 100).toFixed(0) + '%'}
                      </div>
                    </td>
                    <td style={{
                      padding: '1rem',
                      textAlign: 'center',
                      color: '#9b59b6',
                      fontWeight: '600'
                    }}>
                      {stat.accountCount}
                    </td>
                    <td style={{
                      padding: '1rem',
                      textAlign: 'center',
                      color: '#4ecca3',
                      fontWeight: '600'
                    }}>
                      {stat.activeAccountCount}
                    </td>
                    <td style={{
                      padding: '1rem',
                      textAlign: 'right',
                      color: '#3498db',
                      fontWeight: '700',
                      fontFamily: '"JetBrains Mono", monospace'
                    }}>
                      {formatCurrency(stat.totalEarned)}
                    </td>
                    <td style={{
                      padding: '1rem',
                      textAlign: 'right',
                      color: '#4ecca3',
                      fontFamily: '"JetBrains Mono", monospace'
                    }}>
                      {formatCurrency(stat.totalPaid)}
                    </td>
                    <td style={{
                      padding: '1rem',
                      textAlign: 'right',
                      color: '#f39c12',
                      fontWeight: '600',
                      fontFamily: '"JetBrains Mono", monospace'
                    }}>
                      {formatCurrency(stat.totalOwed)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Monthly Totals View */}
      {selectedView === 'monthly' && (
        <div className="reports-table" style={{
          background: '#1a1a1a',
          borderRadius: '12px',
          border: '1px solid #2a2a2a',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Month
                </th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Owed
                </th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Paid
                </th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Total
                </th>
                <th style={{ textAlign: 'center', padding: '1rem', color: '#888', fontWeight: '500' }}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {monthlyTotals.map(month => {
                const isPaidUp = month.owed === 0 && month.total > 0;
                const hasActivity = month.total > 0;

                return (
                  <tr
                    key={month.month}
                    style={{ borderBottom: '1px solid #2a2a2a' }}
                  >
                    <td style={{ padding: '1rem' }}>
                      <div style={{ fontWeight: '600', color: '#e0e0e0' }}>
                        {getMonthLabel(month.month)}
                      </div>
                    </td>
                    <td style={{
                      padding: '1rem',
                      textAlign: 'right',
                      color: '#f39c12',
                      fontFamily: '"JetBrains Mono", monospace'
                    }}>
                      {formatCurrency(month.owed)}
                    </td>
                    <td style={{
                      padding: '1rem',
                      textAlign: 'right',
                      color: '#4ecca3',
                      fontFamily: '"JetBrains Mono", monospace'
                    }}>
                      {formatCurrency(month.paid)}
                    </td>
                    <td style={{
                      padding: '1rem',
                      textAlign: 'right',
                      color: '#3498db',
                      fontWeight: '700',
                      fontFamily: '"JetBrains Mono", monospace'
                    }}>
                      {formatCurrency(month.total)}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      {hasActivity && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.375rem',
                          padding: '0.375rem 0.75rem',
                          background: isPaidUp ? '#4ecca320' : '#f39c1220',
                          color: isPaidUp ? '#4ecca3' : '#f39c12',
                          borderRadius: '6px',
                          fontSize: '0.8rem',
                          fontWeight: '600'
                        }}>
                          {isPaidUp ? <CheckCircle size={14} /> : <Clock size={14} />}
                          {isPaidUp ? 'Paid Up' : 'Outstanding'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
